import React, { useState, Fragment } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import { COLORS } from "utils/styles";
import HoverIconButton from "components/HoverIconButton";
import ConfirmIconButton from "components/ConfirmIconButton";
import ModelSearchList from "components/ModelSearchList";
import FieldError from "components/FieldError";
import classnames from "classnames";
import { ATTACHMENT_REMOVAL } from "utils/constants/confirm";
import {
  attachmentSizeLabel,
  getMimeTypeLabel,
  prepareDownloadAttachmentList
} from "utils/attachment";
import { ATTACHMENT } from "utils/constants/models";
import { useIsAuthed, useAttachments } from "utils/selectors";
import Overlay from "components/Overlay";
import SlimCTA from "components/SlimCTA";
import AttachmentOption from "components/AttachmentOption";
import AttachmentCreate from "components/AttachmentForm/Create";
import {
  LIST_PRESENTATION,
  EXTENDED_OVERLAY_CONTENT,
  secondaryBackButton
} from "utils/constants/ui";
import Pill from "components/Pill";
import { PILL_THEME } from "components/Pill";
import { PRODUCT_FEATURE_CAPABILITY } from "utils/constants/capability";
import EntitlementsUpgradeContainer from "components/EntitlementsUpgradeContainer";

const MODEL_SEARCH_CLASSES = {
  container: "",
  option: {
    option: "f6"
  }
};

const PICK_INTENT = {
  ATTACHMENT: "attachment",
  NEW_ATTACHMENT: "new_attachment"
};

const CheckoutAttachmentPicker = ({
  name,
  editMode,
  arrayHelpers,
  modelIx,
  showOnMount
}) => {
  const isAuthed = useIsAuthed();
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  /**
   * Scaffolded form attachment is a placeholder uuid which gets replaced with a selected attachment
   */
  const attachmentUUID = get(values, name);

  /**
   * Allow the user to toggle between preview and edit mode
   */
  const [showOverlay, setShowOverlay] = useState(showOnMount);
  /**
   * Allow the user to toggle between product and price pick intent
   */
  const [intent, setIntent] = useState(PICK_INTENT.ATTACHMENT);

  /**
   * Setup named intent consts
   */
  const pickAttachment = intent === PICK_INTENT.ATTACHMENT && editMode;
  const newAttachment =
    intent === PICK_INTENT.NEW_ATTACHMENT && isAuthed && editMode;

  const attachments = useAttachments();
  const models = prepareDownloadAttachmentList({ attachments });

  const activeAttachment = attachments.find(
    (model) => model.uuid === attachmentUUID
  );

  const activeModelIds = activeAttachment ? [activeAttachment.id] : [];

  const onSelect = (selection) => {
    if (attachmentUUID !== selection) {
      setFieldValue(name, selection);
      setFieldTouched(name, true);
    }
    setShowOverlay(false);
  };

  let overlayContent = null;

  if (newAttachment) {
    const buttonProps = secondaryBackButton({
      onClick: () => setIntent(PICK_INTENT.ATTACHMENT),
      tooltip: "All files"
    });

    const createProps = {
      header: (
        <div className="flex flex-row items-center">
          <HoverIconButton {...buttonProps} />
        </div>
      ),
      /**
       * Override default afterCreate handler to allow for product selection
       * - instead of default navigation (i.e. go back / go show)
       * @param {Error} err
       * @param {Object<{attachments: Array}>} response
       */
      afterCreate: (err, response) => {
        if (!err) {
          const newAttachmentUUID = response.uuid;
          if (newAttachmentUUID) {
            setTimeout(() => onSelect(newAttachmentUUID), 0);
          } else {
            throw new Error("No attachment id for after create");
          }
        }
      }
    };

    overlayContent = <AttachmentCreate {...createProps} />;
  } else if (pickAttachment) {
    let newActions;
    let emptyComponent;
    if (isAuthed && editMode) {
      newActions = [
        {
          theme: "slim",
          copy: "New",
          loading: false,
          onClick: () => setIntent(PICK_INTENT.NEW_ATTACHMENT)
        }
      ];
      emptyComponent = (
        <SlimCTA
          header="No files"
          subheader="Upload a file to get started"
          actions={newActions}
        />
      );
    }

    overlayContent = (
      <ModelSearchList
        models={models}
        query={{
          model: ATTACHMENT,
          keys: ["name"]
        }}
        customClasses={{
          [LIST_PRESENTATION.GRID]: {
            ...MODEL_SEARCH_CLASSES,
            list: {
              list: "grid-container-2 ma0 pa0 list",
              item: "w-100"
            }
          },
          [LIST_PRESENTATION.LIST]: { ...MODEL_SEARCH_CLASSES }
        }}
        components={{
          [LIST_PRESENTATION.GRID]: AttachmentOption,
          [LIST_PRESENTATION.LIST]: AttachmentOption
        }}
        /**
         * Allow User to create a new Attachment when
         * - Authenticated
         */
        barActions={newActions}
        emptyComponent={emptyComponent}
        activeModelIds={activeModelIds}
        onClick={onSelect}
      />
    );
  }

  const activeAttachmentInfo = activeAttachment ? (
    <div className="flex flex-row items-center">
      <div className="fw5 f5 pl0 pr2 ph2-ns bl-ns hairline-1">
        {attachmentSizeLabel(activeAttachment.data.size)}
      </div>
      {activeAttachment.data.mimetype && (
        <div className="pl2 bl hairline-1 pr2">
          <Pill
            theme={PILL_THEME.SLIM}
            copy={getMimeTypeLabel(activeAttachment.data.mimetype)}
          />
        </div>
      )}
    </div>
  ) : null;

  return (
    <Fragment>
      <div className="w-100 flex flex-column ba br2 hairline-1">
        <div className="flex flex-column flex-row-ns justify-between items-center-ns">
          <div
            className={classnames(
              "flex flex-row justify-between items-center w-100",
              {
                "bg-white--hover": editMode
              }
            )}
          >
            <div
              className={classnames("flex flex-row w-100 pa3", {
                pointer: editMode
              })}
              onClick={() => editMode && setShowOverlay(!showOverlay)}
            >
              <div
                className={classnames("fw5 f5 pr2 lh-title", {
                  "color-primary": !activeAttachment
                })}
              >
                {activeAttachment
                  ? activeAttachment.data.filename
                  : "Select a file"}
              </div>
              <div className="dn flex-ns flex-row items-center">
                {activeAttachmentInfo}
              </div>
            </div>
            <div className="pr3 flex flex-row items-center justify-end">
              {editMode && (
                <Fragment>
                  <div className="ph2">
                    <ConfirmIconButton
                      slides={ATTACHMENT_REMOVAL}
                      onConfirm={() => {
                        arrayHelpers.remove(modelIx);
                        if (!get(touched, name)) {
                          setFieldTouched(name, true);
                        }
                      }}
                    />
                  </div>
                  <HoverIconButton
                    onClick={() => editMode && setShowOverlay(!showOverlay)}
                    tooltip={!showOverlay && "Show details"}
                    color={showOverlay ? COLORS.blue : COLORS.primary}
                    type={showOverlay ? "eye" : "eyeClosed"}
                  />
                </Fragment>
              )}
            </div>
          </div>
          <div
            onClick={() => editMode && setShowOverlay(!showOverlay)}
            className={classnames(
              "flex dn-ns flex-row justify-between items-center ph3 pb3",
              {
                "bb hairline-1": showOverlay
              }
            )}
          >
            <div className="flex flex-row">{activeAttachmentInfo}</div>
          </div>
        </div>
        {showOverlay && overlayContent && (
          <Overlay
            hideOverlay={() => setShowOverlay(false)}
            customClasses={{ content: EXTENDED_OVERLAY_CONTENT }}
          >
            <div className="pt3">
              <div className="f3 fw6 ph3 pb2">Select a file</div>
              <EntitlementsUpgradeContainer
                features={[PRODUCT_FEATURE_CAPABILITY.ATTACH_FILES]}
              >
                {(content) =>
                  content ? (
                    <div className="w-100 ph3 pb3 pb0-ns">{content}</div>
                  ) : null
                }
              </EntitlementsUpgradeContainer>

              {overlayContent}
            </div>
          </Overlay>
        )}
      </div>
      <FieldError name={name} />
    </Fragment>
  );
};

export default CheckoutAttachmentPicker;
